import type { ReactNode } from 'react'
import type { CashPolicyCadence, CashPolicyResult } from '../engine/types'
import { monthLabel } from '../monthColumns'
import { formatCurrency, formatCurrencyWhole } from '../projectionsFormat'

type PolicySummaryTilesProps = {
  summary: CashPolicyResult['summary']
  amountPerPeriod: number
  cadence: CashPolicyCadence
  minOperatingCashMonth: string
  lastClosedMonth: string
}

const LABEL = 'text-xs text-gray-500'
const VALUE = 'text-xs font-medium tabular-nums text-gray-900'
const VALUE_BREACH = 'text-xs font-medium tabular-nums text-amber-800'
const SUB = 'text-xs leading-4 tabular-nums text-gray-500'

const CADENCE_SUFFIX: Record<CashPolicyCadence, string> = {
  biweekly: '/ 2 wks',
  monthly: '/ mo',
}

function Tile({
  label,
  tinted = false,
  children,
}: {
  label: string
  tinted?: boolean
  children: ReactNode
}) {
  return (
    <div className={`p-3 ${tinted ? 'bg-amber-50' : 'bg-white'}`}>
      <div className={LABEL}>{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  )
}

export default function PolicySummaryTiles({
  summary,
  amountPerPeriod,
  cadence,
  minOperatingCashMonth,
  lastClosedMonth,
}: PolicySummaryTilesProps) {
  const breachCount = summary.breach_months.length
  const hasBreach = breachCount > 0
  const lowMonth =
    minOperatingCashMonth || monthLabel(lastClosedMonth, summary.min_operating_cash_t)
  const firstBreachT = summary.breach_months[0]
  const firstBreachMonth =
    firstBreachT == null ? null : monthLabel(lastClosedMonth, firstBreachT)

  const mmBalances: Array<{ at: string; value: number }> = [
    { at: '+12', value: summary.mm_balance_t12 },
    { at: '+24', value: summary.mm_balance_t24 },
    { at: '+36', value: summary.mm_balance_t36 },
  ]

  return (
    <div>
      <div className="grid grid-cols-3 gap-px overflow-hidden rounded border border-gray-200 bg-gray-200">
        <Tile label="Distribution per period">
          <span className={VALUE}>{formatCurrencyWhole(amountPerPeriod)}</span>
          <span className="ml-1 text-xs text-gray-500">{CADENCE_SUFFIX[cadence]}</span>
        </Tile>

        <Tile label="Distributions next 12 mo">
          <div className={VALUE}>{formatCurrencyWhole(summary.distributions_12m)}</div>
        </Tile>

        <Tile label="Swept to MM next 12 mo">
          <div className={VALUE}>{formatCurrencyWhole(summary.sweep_12m)}</div>
          <div className={SUB}>Interest {formatCurrencyWhole(summary.interest_12m)}</div>
        </Tile>

        <Tile label="MM balance">
          <dl className="space-y-0.5">
            {mmBalances.map((row) => (
              <div key={row.at} className="flex items-baseline justify-between gap-2">
                <dt className="text-xs tabular-nums text-gray-500">{row.at}</dt>
                <dd className={VALUE}>{formatCurrency(row.value)}</dd>
              </div>
            ))}
          </dl>
        </Tile>

        <Tile label="Lowest operating cash">
          <div className={VALUE}>{formatCurrency(summary.min_operating_cash)}</div>
          <div className={SUB}>{lowMonth}</div>
        </Tile>

        <Tile label="Breach months" tinted={hasBreach}>
          <div className={hasBreach ? VALUE_BREACH : VALUE}>
            {hasBreach ? `${breachCount} ${breachCount === 1 ? 'month' : 'months'}` : 'None'}
          </div>
          {hasBreach ? (
            <>
              <div className={SUB}>
                Largest shortfall {formatCurrencyWhole(summary.max_shortfall)}
              </div>
              {firstBreachMonth ? <div className={SUB}>First {firstBreachMonth}</div> : null}
            </>
          ) : (
            <div className={SUB}>Operating cash holds the floor</div>
          )}
        </Tile>
      </div>
    </div>
  )
}
